import { hash256, ripemd160 as internalRipemd160, secp256k1 as internalSecp256k1, sha1 as internalSha1, sha256 as internalSha256, } from '../../../../crypto/crypto.js';
import { binToHex } from '../../../../format/format.js';
import { applyError, AuthenticationErrorCommon, booleanToVmNumber, combineOperations, ConsensusCommon, decodeBitcoinSignature, encodeAuthenticationInstructions, generateSigningSerializationBCH, isValidPublicKeyEncoding, isValidSignatureEncodingBCHRaw, isValidSignatureEncodingBCHTransaction, opVerify, pushToStack, useOneStackItem, useOneVmNumber, useThreeStackItems, useTwoStackItems, } from '../../common/common.js';
import { SigningSerializationTypesBCH2023 } from '../2023/bch-2023.js';
import { AuthenticationErrorBCHCHIPs } from './bch-chips-errors.js';
import { ConsensusBCHCHIPs } from './bch-chips-types.js';
/**
 * Given a message length, compute and return the number of hash digest
 * iterations required. (See `CHIP-2021-05-vm-limits`)
 */
export const hashDigestIterations = (messageLength) => 
// eslint-disable-next-line no-bitwise, @typescript-eslint/no-magic-numbers
1 + (((messageLength + 8) / 64) | 0);
/**
 * Given a program state, increment the hash digest iteration count for a
 * message of the provided length. If the total would exceed the maximum, append
 * an error.
 *
 * @param state - the program state
 * @param messageLength - the message length
 * @param operation - the operation to execute if no error occurred
 */
export const incrementHashDigestIterations = (state, messageLength, operation) => {
    const requiredTotalIterations = state.hashDigestIterations + hashDigestIterations(messageLength);
    if (requiredTotalIterations > ConsensusBCHCHIPs.maximumHashDigestIterations) {
        return applyError(state, AuthenticationErrorBCHCHIPs.excessiveHashing, `Required cumulative iterations: ${requiredTotalIterations}`);
    }
    return operation(state);
};
export const opRipemd160ChipLimits = ({ ripemd160, } = { ripemd160: internalRipemd160 }) => (state) => useOneStackItem(state, (nextState, [value]) => incrementHashDigestIterations(nextState, value.length, (finalState) => pushToStack(finalState, ripemd160.hash(value))));
export const opSha1ChipLimits = ({ sha1, } = { sha1: internalSha1 }) => (state) => useOneStackItem(state, (nextState, [value]) => incrementHashDigestIterations(nextState, value.length, (finalState) => pushToStack(finalState, sha1.hash(value))));
export const opSha256ChipLimits = ({ sha256, } = { sha256: internalSha256 }) => (state) => useOneStackItem(state, (nextState, [value]) => incrementHashDigestIterations(nextState, value.length, (finalState) => pushToStack(finalState, sha256.hash(value))));
export const opHash160ChipLimits = ({ ripemd160, sha256, } = { ripemd160: internalRipemd160, sha256: internalSha256 }) => (state) => useOneStackItem(state, (nextState, [value]) => incrementHashDigestIterations(nextState, value.length, (finalState) => pushToStack(finalState, ripemd160.hash(sha256.hash(value)))));
export const opHash256ChipLimits = ({ sha256, } = { sha256: internalSha256 }) => (state) => useOneStackItem(state, (nextState, [value]) => incrementHashDigestIterations(nextState, value.length, (finalState) => pushToStack(finalState, hash256(value, sha256))));
export const opCheckSigChipLimits = ({ secp256k1, sha256, } = { secp256k1: internalSecp256k1, sha256: internalSha256 }) => (s) => 
// eslint-disable-next-line complexity
useTwoStackItems(s, (state, [bitcoinEncodedSignature, publicKey]) => {
    if (!isValidPublicKeyEncoding(publicKey)) {
        return applyError(state, AuthenticationErrorCommon.invalidPublicKeyEncoding);
    }
    if (!isValidSignatureEncodingBCHTransaction(bitcoinEncodedSignature, SigningSerializationTypesBCH2023)) {
        return applyError(state, AuthenticationErrorCommon.invalidSignatureEncoding, `Transaction signature (including signing serialization type): ${binToHex(bitcoinEncodedSignature)}`);
    }
    const coveredBytecode = encodeAuthenticationInstructions(state.instructions).subarray(state.lastCodeSeparator + 1);
    const { signingSerializationType, signature } = decodeBitcoinSignature(bitcoinEncodedSignature);
    const serialization = generateSigningSerializationBCH(state.program, { coveredBytecode, signingSerializationType }, sha256);
    const requiredTotalIterations = state.hashDigestIterations + hashDigestIterations(serialization.length);
    if (requiredTotalIterations > ConsensusBCHCHIPs.maximumHashDigestIterations) {
        return applyError(state, AuthenticationErrorBCHCHIPs.excessiveHashing, `Required cumulative iterations: ${requiredTotalIterations}`);
    }
    const digest = hash256(serialization, sha256);
    // eslint-disable-next-line functional/no-expression-statement, functional/immutable-data
    state.signedMessages.push({ digest, serialization });
    const useSchnorr = signature.length === ConsensusCommon.schnorrSignatureLength;
    const success = useSchnorr
        ? secp256k1.verifySignatureSchnorr(signature, publicKey, digest)
        : secp256k1.verifySignatureDERLowS(signature, publicKey, digest);
    return !success && signature.length !== 0
        ? applyError(state, AuthenticationErrorCommon.nonNullSignatureFailure)
        : pushToStack(state, booleanToVmNumber(success));
});
// TODO: implement schnorr multisig https://gitlab.com/bitcoin-cash-node/bchn-sw/bitcoincash-upgrade-specifications/-/blob/master/spec/2019-11-15-schnorrmultisig.md
export const opCheckMultiSigChipLimits = ({ secp256k1, sha256, } = { secp256k1: internalSecp256k1, sha256: internalSha256 }) => (s) => useOneVmNumber(s, (state, publicKeysValue) => {
    const potentialPublicKeys = Number(publicKeysValue);
    if (potentialPublicKeys < 0) {
        return applyError(state, AuthenticationErrorCommon.invalidNaturalNumber);
    }
    if (potentialPublicKeys > 20 /* Multisig.maximumPublicKeys */) {
        return applyError(state, AuthenticationErrorCommon.exceedsMaximumMultisigPublicKeyCount);
    }
    const publicKeys = 
    // eslint-disable-next-line functional/immutable-data
    potentialPublicKeys > 0 ? state.stack.splice(-potentialPublicKeys) : [];
    return useOneVmNumber(state, (nextState, approvingKeys) => {
        const requiredApprovingPublicKeys = Number(approvingKeys);
        if (requiredApprovingPublicKeys < 0) {
            return applyError(nextState, AuthenticationErrorCommon.invalidNaturalNumber);
        }
        if (requiredApprovingPublicKeys > potentialPublicKeys) {
            return applyError(nextState, AuthenticationErrorCommon.insufficientPublicKeys);
        }
        const signatures = requiredApprovingPublicKeys > 0
            ? // eslint-disable-next-line functional/immutable-data
                nextState.stack.splice(-requiredApprovingPublicKeys)
            : [];
        return useOneStackItem(nextState, 
        // eslint-disable-next-line complexity
        (finalState, [protocolBugValue]) => {
            if (protocolBugValue.length !== 0) {
                return applyError(finalState, AuthenticationErrorCommon.invalidProtocolBugValue);
            }
            const coveredBytecode = encodeAuthenticationInstructions(finalState.instructions).subarray(finalState.lastCodeSeparator + 1);
            let approvingPublicKeys = 0; // eslint-disable-line functional/no-let
            let remainingSignatures = signatures.length; // eslint-disable-line functional/no-let
            let remainingPublicKeys = publicKeys.length; // eslint-disable-line functional/no-let
            // eslint-disable-next-line functional/no-loop-statement
            while (remainingSignatures > 0 &&
                remainingPublicKeys > 0 &&
                approvingPublicKeys + remainingPublicKeys >=
                    remainingSignatures &&
                approvingPublicKeys !== requiredApprovingPublicKeys) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const publicKey = publicKeys[remainingPublicKeys - 1];
                const bitcoinEncodedSignature = 
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                signatures[remainingSignatures - 1];
                if (!isValidPublicKeyEncoding(publicKey)) {
                    return applyError(finalState, AuthenticationErrorCommon.invalidPublicKeyEncoding);
                }
                if (!isValidSignatureEncodingBCHTransaction(bitcoinEncodedSignature, SigningSerializationTypesBCH2023)) {
                    return applyError(finalState, AuthenticationErrorCommon.invalidSignatureEncoding, `Transaction signature (including signing serialization type): ${binToHex(bitcoinEncodedSignature)}`);
                }
                const { signingSerializationType, signature } = decodeBitcoinSignature(bitcoinEncodedSignature);
                const serialization = generateSigningSerializationBCH(state.program, { coveredBytecode, signingSerializationType }, sha256);
                const requiredTotalIterations = state.hashDigestIterations +
                    hashDigestIterations(serialization.length);
                if (requiredTotalIterations >
                    ConsensusBCHCHIPs.maximumHashDigestIterations) {
                    return applyError(state, AuthenticationErrorBCHCHIPs.excessiveHashing, `Required cumulative iterations: ${requiredTotalIterations}`);
                }
                const digest = hash256(serialization, sha256);
                // eslint-disable-next-line functional/no-expression-statement, functional/immutable-data
                finalState.signedMessages.push({ digest, serialization });
                if (signature.length === ConsensusCommon.schnorrSignatureLength) {
                    return applyError(finalState, AuthenticationErrorCommon.schnorrSizedSignatureInCheckMultiSig);
                }
                const signed = secp256k1.verifySignatureDERLowS(signature, publicKey, digest);
                // eslint-disable-next-line functional/no-conditional-statement
                if (signed) {
                    approvingPublicKeys += 1; // eslint-disable-line functional/no-expression-statement
                    remainingSignatures -= 1; // eslint-disable-line functional/no-expression-statement
                }
                remainingPublicKeys -= 1; // eslint-disable-line functional/no-expression-statement
            }
            const success = approvingPublicKeys === requiredApprovingPublicKeys;
            if (!success &&
                !signatures.every((signature) => signature.length === 0)) {
                return applyError(finalState, AuthenticationErrorCommon.nonNullSignatureFailure);
            }
            return pushToStack(finalState, booleanToVmNumber(success));
        });
    });
});
export const opCheckSigVerifyChipLimits = ({ secp256k1, sha256, } = { secp256k1: internalSecp256k1, sha256: internalSha256 }) => combineOperations(opCheckSigChipLimits({ secp256k1, sha256 }), opVerify);
export const opCheckMultiSigVerifyChipLimits = ({ secp256k1, sha256, }) => combineOperations(opCheckMultiSigChipLimits({ secp256k1, sha256 }), opVerify);
export const opCheckDataSigChipLimits = ({ secp256k1, sha256, }) => (state) => 
// eslint-disable-next-line complexity
useThreeStackItems(state, (nextState, [signature, message, publicKey]) => {
    if (!isValidSignatureEncodingBCHRaw(signature)) {
        return applyError(nextState, AuthenticationErrorCommon.invalidSignatureEncoding, `Data signature: ${binToHex(signature)}`);
    }
    if (!isValidPublicKeyEncoding(publicKey)) {
        return applyError(nextState, AuthenticationErrorCommon.invalidPublicKeyEncoding);
    }
    const requiredTotalIterations = state.hashDigestIterations + hashDigestIterations(message.length);
    if (requiredTotalIterations > ConsensusBCHCHIPs.maximumHashDigestIterations) {
        return applyError(state, AuthenticationErrorBCHCHIPs.excessiveHashing, `Required cumulative iterations: ${requiredTotalIterations}`);
    }
    const digest = sha256.hash(message);
    // eslint-disable-next-line functional/no-expression-statement, functional/immutable-data
    nextState.signedMessages.push({ digest, message });
    const useSchnorr = signature.length === ConsensusCommon.schnorrSignatureLength;
    const success = useSchnorr
        ? secp256k1.verifySignatureSchnorr(signature, publicKey, digest)
        : secp256k1.verifySignatureDERLowS(signature, publicKey, digest);
    return !success && signature.length !== 0
        ? applyError(nextState, AuthenticationErrorCommon.nonNullSignatureFailure)
        : pushToStack(nextState, booleanToVmNumber(success));
});
export const opCheckDataSigVerifyChipLimits = ({ secp256k1, sha256, } = { secp256k1: internalSecp256k1, sha256: internalSha256 }) => combineOperations(opCheckDataSigChipLimits({ secp256k1, sha256 }), opVerify);
//# sourceMappingURL=bch-chips-crypto.js.map